import {
    createAsyncThunk,
    createSelector,
    createSlice,
} from "@reduxjs/toolkit";
import { languageApi, LanguageDef } from "../../services/apis/language.api";
import { ASSESSMENT_LANGUAGE_LABELS } from "../../utils/assessmentLanguage";
import type { RootState } from "../store";

export interface LanguageState {
    languages: LanguageDef[];
    status: "idle" | "loading" | "ready" | "failed";
}

const initialState: LanguageState = {
    languages: [],
    status: "idle",
};

/**
 * Loads the language registry. Only an idle registry starts a request, so
 * several hooks mounting in the same render (or React StrictMode running an
 * effect twice) cost one call, and a failed load stays failed instead of
 * retrying.
 */
export const fetchLanguages = createAsyncThunk<
    LanguageDef[],
    void,
    { state: RootState }
>("language/fetch", () => languageApi.list(), {
    condition: (_arg, { getState }) => getState().language.status === "idle",
});

const slice = createSlice({
    name: "language",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchLanguages.pending, (state) => {
            state.status = "loading";
        });
        builder.addCase(fetchLanguages.fulfilled, (state, action) => {
            state.languages = action.payload;
            state.status = "ready";
        });
        builder.addCase(fetchLanguages.rejected, (state) => {
            state.status = "failed";
        });
    },
});

const NO_LANGUAGES: LanguageDef[] = [];

/** The registry, or an empty list until it has loaded. */
export const selectLanguages = (state: RootState): LanguageDef[] =>
    state.language.status === "ready" ? state.language.languages : NO_LANGUAGES;

export const selectLanguageStatus = (
    state: RootState
): LanguageState["status"] => state.language.status;

/**
 * Code to display name. Once the registry has loaded this is each language's
 * native name; before that, or if the request failed, it is the static launch
 * labels, so a picker is never empty. Memoised, so the object only changes
 * when the registry does.
 */
export const selectLanguageLabels = createSelector(
    [selectLanguages],
    (languages): Record<string, string> => {
        if (!languages.length) return ASSESSMENT_LANGUAGE_LABELS;
        const labels: Record<string, string> = {};
        for (const language of languages) {
            labels[language.code] =
                language.nativeName || language.name || language.code;
        }
        return labels;
    }
);

export const languageReducer = slice.reducer;
export default slice.reducer;
